import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, ArrowLeft, Eye, X, User, LogOut, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PengawasDashboard() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPetugas, setSelectedPetugas] = useState("");
  const [assignedPetugas, setAssignedPetugas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewingDetail, setViewingDetail] = useState(null);
  
  const userName = localStorage.getItem("user_name") || "";
  const userRole = localStorage.getItem("user_role") || "";
  const isLoggedIn = !!localStorage.getItem("user_token");

  useEffect(() => {
    if (!isLoggedIn || userRole !== "pengawas") {
      toast.error("Akses ditolak");
      navigate("/login");
      return;
    }
    fetchAssignedPetugas();
  }, [isLoggedIn, userRole, navigate]);

  const fetchAssignedPetugas = async () => {
    try {
      const response = await axios.get(`${API}/pengawas/assigned-petugas`, {
        headers: getAuthHeader()
      });
      setAssignedPetugas(response.data.petugas || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data petugas (unauthorized).");
      } else {
        toast.error("Gagal memuat data petugas");
      }
    }
  };

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedPetugas) params.append("petugas_id", selectedPetugas);
      
      const response = await axios.get(`${API}/user/reports?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setReports(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Gagal memuat laporan (unauthorized).");
      } else {
        toast.error("Gagal memuat laporan");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_balance");
    toast.success("Berhasil logout");
    // Use window.location for full page refresh to clear React state
    window.location.href = "/login";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getServiceLabel = (type) => {
    const labels = {
      pdam: "PDAM",
      token_listrik: "Token Listrik",
      tagihan_listrik: "Tagihan Listrik",
      pulsa: "Pulsa"
    };
    return labels[type] || type;
  };

  const exportExcel = () => {
    if (!reports || !reports.transactions.length) {
      toast.error("Tidak ada data untuk di-export");
      return;
    }

    const formatRupiah = (amount) => `Rp ${new Intl.NumberFormat("id-ID").format(amount || 0)}`;

    const wsData = [
      [`LAPORAN PENGAWAS - ${userName}`],
      [`Periode: ${startDate || 'Semua'} s/d ${endDate || 'Semua'}`],
      [`Tanggal Export: ${new Date().toLocaleString("id-ID")}`],
      [],
      ["No", "Tanggal", "Petugas", "Layanan", "No. Pelanggan", "Nama", "Harga", "Biaya Admin", "Total", "Status"],
    ];

    reports.transactions.forEach((t, i) => {
      wsData.push([
        i + 1,
        formatDate(t.created_at),
        t.user_name || "-",
        getServiceLabel(t.service_type),
        t.customer_no || "",
        t.customer_name || "",
        formatRupiah(t.amount),
        formatRupiah(t.admin_fee),
        formatRupiah(t.total),
        t.status === 'success' ? 'Sukses' : t.status
      ]);
    });

    wsData.push([]);
    wsData.push(["", "", "", "", "", "TOTAL:", 
      formatRupiah(reports.summary.total_amount),
      formatRupiah(reports.summary.total_admin_fee),
      formatRupiah(reports.summary.total_amount + reports.summary.total_admin_fee),
      ""
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 5 }, { wch: 18 }, { wch: 15 }, { wch: 14 }, { wch: 16 }, { wch: 20 },
      { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 10 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Laporan Petugas");
    XLSX.writeFile(wb, `laporan_pengawas_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("File Excel berhasil di-download");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-6 h-6 text-purple-600" />
            <span className="text-lg font-heading font-semibold text-slate-800">Dashboard Pengawas</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700">
              <User className="w-4 h-4" />
              <span className="font-medium text-sm">{userName}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-heading font-bold text-slate-800 mb-6 flex items-center gap-3">
            <FileText className="w-7 h-7 text-purple-600" />
            Laporan Petugas
          </h1>

          {/* Info: Assigned Petugas */}
          {assignedPetugas.length === 0 ? (
            <Card className="mb-6 border-amber-200 bg-amber-50">
              <CardContent className="p-6 text-center">
                <Eye className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="font-semibold text-amber-800 mb-2">Belum Ada Petugas</h3>
                <p className="text-amber-600 text-sm">
                  Admin belum meng-assign petugas kepada Anda. Hubungi admin untuk mendapatkan akses ke laporan petugas.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filter */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        Tanggal Mulai
                      </label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-11"
                        data-testid="start-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        Tanggal Akhir
                      </label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-11"
                        data-testid="end-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        <Filter className="w-4 h-4 inline mr-1" />
                        Petugas
                      </label>
                      <select
                        value={selectedPetugas}
                        onChange={(e) => setSelectedPetugas(e.target.value)}
                        className="w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="petugas-filter-select"
                      >
                        <option value="">Semua Petugas ({assignedPetugas.length})</option>
                        {assignedPetugas.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button onClick={fetchReports} className="h-11 px-6 bg-purple-600 hover:bg-purple-700" data-testid="filter-btn">
                      Filter
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={exportExcel} 
                      className="h-11 px-6 gap-2"
                      disabled={!reports || reports.transactions.length === 0}
                      data-testid="export-btn"
                    >
                      <Download className="w-4 h-4" />
                      Export Excel
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              {reports && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                    <CardContent className="p-5">
                      <p className="text-sm text-purple-600 mb-1">Total Transaksi</p>
                      <p className="text-2xl font-heading font-bold text-purple-700">
                        {reports.summary.total_transactions}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                    <CardContent className="p-5">
                      <p className="text-sm text-emerald-600 mb-1">Total Nilai Transaksi</p>
                      <p className="text-xl font-heading font-bold text-emerald-700">
                        {formatCurrency(reports.summary.total_amount)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                    <CardContent className="p-5">
                      <p className="text-sm text-amber-600 mb-1">Total Biaya Admin</p>
                      <p className="text-xl font-heading font-bold text-amber-700">
                        {formatCurrency(reports.summary.total_admin_fee)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Transactions Table */}
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner" />
                </div>
              ) : !reports ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Calendar className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                    <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                      Pilih Filter
                    </h3>
                    <p className="text-slate-600">
                      Pilih tanggal dan klik "Filter" untuk melihat laporan petugas
                    </p>
                  </CardContent>
                </Card>
              ) : reports.transactions.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                      Tidak Ada Transaksi
                    </h3>
                    <p className="text-slate-600">
                      Tidak ada transaksi pada periode ini
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      Detail Transaksi Petugas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left p-3 font-semibold text-slate-600">No</th>
                            <th className="text-left p-3 font-semibold text-slate-600">Tanggal</th>
                            <th className="text-left p-3 font-semibold text-slate-600">Petugas</th>
                            <th className="text-left p-3 font-semibold text-slate-600">Layanan</th>
                            <th className="text-left p-3 font-semibold text-slate-600">Pelanggan</th>
                            <th className="text-right p-3 font-semibold text-slate-600">Total</th>
                            <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.transactions.map((txn, index) => (
                            <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="p-3 text-slate-500">{index + 1}</td>
                              <td className="p-3 text-slate-600 text-xs">
                                {formatDate(txn.created_at)}
                              </td>
                              <td className="p-3">
                                <span className="text-xs px-2 py-1 rounded-full bg-sky-100 text-sky-700 font-medium">
                                  {txn.user_name || "-"}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium
                                  ${txn.service_type === 'pdam' ? 'bg-sky-100 text-sky-700' : ''}
                                  ${txn.service_type === 'token_listrik' ? 'bg-amber-100 text-amber-700' : ''}
                                  ${txn.service_type === 'pulsa' ? 'bg-emerald-100 text-emerald-700' : ''}
                                `}>
                                  {getServiceLabel(txn.service_type)}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="font-mono text-xs text-slate-500">{txn.customer_no}</div>
                                <div className="text-slate-800 font-medium truncate max-w-[150px]">{txn.customer_name}</div>
                              </td>
                              <td className="p-3 text-right font-semibold text-purple-600">
                                {formatCurrency(txn.total)}
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-xs px-2 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700">
                                  Sukses
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 font-semibold">
                          <tr>
                            <td colSpan="5" className="p-3 text-right text-slate-600">TOTAL:</td>
                            <td className="p-3 text-right text-purple-600">
                              {formatCurrency(reports.summary.total_amount + reports.summary.total_admin_fee)}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
